import { Dispatch, SetStateAction } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";
import { FilterBuilder } from "../../../helpers/ThunkHelper/FilterBuilder";

export type DashboardColFilter = {
  filterField: string;
  filterValue: any;
};

/** Merge KPI/base filters with column funnel filters from MasterTableContainer. */
export function mergeDashboardFilters(
  baseFilters: Record<string, any> = {},
  filterValue: DashboardColFilter[] = [],
) {
  const built = FilterBuilder(filterValue || []);
  return {
    ...baseFilters,
    ...built,
    range: {
      ...(baseFilters.range || {}),
      ...(built.range || {}),
    },
  };
}

/** Client-side filter for endpoints that don't accept column filters in the API. */
export function applyDashboardClientFilters<T extends Record<string, any>>(
  rows: T[],
  filterValue: DashboardColFilter[] = [],
): T[] {
  if (!filterValue?.length) return rows;

  return rows.filter((row) =>
    filterValue.every(({ filterField, filterValue: val }) => {
      if (val === undefined || val === null || val === "") return true;

      const cell = row[filterField];

      if (
        typeof val === "object" &&
        (val.from_date !== undefined || val.to_date !== undefined)
      ) {
        if (!cell) return false;
        const time = new Date(cell).getTime();
        if (Number.isNaN(time)) return false;
        if (val.from_date) {
          const from = new Date(val.from_date).getTime();
          if (time < from) return false;
        }
        if (val.to_date) {
          const to = new Date(`${val.to_date}T23:59:59`).getTime();
          if (time > to) return false;
        }
        return true;
      }

      return String(cell ?? "")
        .toLowerCase()
        .includes(String(val).toLowerCase());
    }),
  );
}

export type DashboardSidebarFilterState = {
  columnFilters: ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  filterValue: DashboardColFilter[];
  setFilterValue: Dispatch<SetStateAction<DashboardColFilter[]>>;
  resetFilters: () => void;
};

/** Scoped filter-dropdown URL for dashboard sidebars (options from current KPI set only). */
export function dashboardScopedFilterDropdown(
  source: string,
  field: string,
  extraQuery?: Record<string, string | undefined>,
) {
  const params = new URLSearchParams({ source, field });
  if (extraQuery) {
    Object.entries(extraQuery).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }
  return `/dashboard/sidebar/filter-dropdown?${params.toString()}`;
}

/** Column filter props: POST scoped dropdown with KPI base filters. */
export function scopedDropdownProps(
  source: string,
  field: string,
  baseFilters: Record<string, any>,
  extraQuery?: Record<string, string | undefined>,
) {
  return {
    filterType: "dropdown" as const,
    filterFetchUrl: dashboardScopedFilterDropdown(source, field, extraQuery),
    filterFetchBody: { filters: baseFilters },
  };
}
